import { IMeetingCategory, IOperationResultNoValue } from '@ViewModels';
import { css } from 'aphrodite';
import { FC, useState } from 'react';
import { toast } from 'sonner';
import {
	invalidateSchedulerConfigsQuery,
	MeetingCategoryFields,
	useMeetingCategoryDeleteMutation,
	useMeetingCategoryUpdateMutation,
} from '../../../../../queries/Scheduler';
import { useDebounceCallback } from '../../../../../hooks/useDebounceValue';
import { Button } from '../../../../components-v2/Button';
import { Input } from '../../../../components-v2/Input';
import { ThemeColors } from '../../../../components-v2/Theme/theme';
import { ColorPicker } from '../../../../containers/PeopleV2/TagsContainer/ColorPicker/ColorPicker';
import { toMeetingCategoryMaxValue as toMaxValue } from './meetingCategoryUtils';
import { styleSheet } from './styles';

interface IProps {
	category: IMeetingCategory;
	forUserId?: string;
	onChanged: () => void;
}

const toMaxInputValue = (value?: number) => (value ? String(value) : '');

export const MeetingCategoryRow: FC<IProps> = ({ category, forUserId, onChanged }) => {
	const [name, setName] = useState(category.name ?? '');
	const [color, setColor] = useState<keyof ThemeColors['pill'] | undefined>(category.color);
	const [maxPerDay, setMaxPerDay] = useState(toMaxInputValue(category.maxPerDay));
	const [maxPerMonth, setMaxPerMonth] = useState(toMaxInputValue(category.maxPerMonth));
	const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

	// Drop whatever the user had typed and show what the server actually holds, so a rejected edit can't sit in
	// the inputs and get pushed back up by the next save.
	const revertToServerValues = () => {
		setName(category.name ?? '');
		setColor(category.color);
		setMaxPerDay(toMaxInputValue(category.maxPerDay));
		setMaxPerMonth(toMaxInputValue(category.maxPerMonth));
	};

	const updateMutation = useMeetingCategoryUpdateMutation({
		// Meeting cards render the category's name and color, and deleting one detaches it from its meetings,
		// so the configs behind those cards have to be refetched too, not just the category list.
		onSuccess: () => {
			invalidateSchedulerConfigsQuery();
			onChanged();
		},
		onError: (error: IOperationResultNoValue) => {
			toast.error(error?.systemMessage ?? `Failed to update ${category.name}`);
			revertToServerValues();
			onChanged();
		},
		scope: { id: `meeting-category-${category.id}` },
	});

	const deleteMutation = useMeetingCategoryDeleteMutation({
		onSuccess: () => {
			toast.success(`${category.name} deleted`);
			invalidateSchedulerConfigsQuery();
			onChanged();
		},
		onError: error => {
			toast.error(error?.systemMessage ?? `Failed to delete ${category.name}`);
			setIsConfirmingDelete(false);
		},
	});

	const save = (fields: Partial<MeetingCategoryFields>) => {
		const merged: MeetingCategoryFields = {
			name,
			color,
			maxPerDay: toMaxValue(maxPerDay),
			maxPerMonth: toMaxValue(maxPerMonth),
			...fields,
		};

		if (!merged.name.trim()) {
			return;
		}

		updateMutation.mutate({ categoryId: category.id, category: merged, forUserId });
	};

	const debouncedSave = useDebounceCallback((fields: Partial<MeetingCategoryFields>) => save(fields), 600);

	// An empty name blocks every save, including edits to the other fields, so it can never be left empty.
	// Restoring it saves as well, otherwise anything typed into the other fields while the name was empty
	// would sit in the inputs having never reached the server.
	const onNameBlur = () => {
		if (name.trim()) {
			return;
		}
		debouncedSave.cancel();
		const restored = category.name ?? '';
		setName(restored);
		save({ name: restored });
	};

	const saveImmediately = (fields: Partial<MeetingCategoryFields>) => {
		debouncedSave.cancel();
		save(fields);
	};

	if (isConfirmingDelete) {
		return (
			<div className={css(styleSheet.row)}>
				<div className={css(styleSheet.confirmRow)}>
					<span className={css(styleSheet.confirmMessage)}>Are you sure you want to delete "{category.name}"?</span>
					<div className={css(styleSheet.confirmActions)}>
						<Button
							variant='destructive'
							size='sm'
							onClick={() => deleteMutation.mutate({ categoryId: category.id, forUserId })}
							isLoading={deleteMutation.isPending}
						>
							Delete
						</Button>
						<Button variant='secondary' size='sm' onClick={() => setIsConfirmingDelete(false)}>
							Cancel
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={css(styleSheet.row)}>
			<Input
				aria-label='Category name'
				styles={[styleSheet.nameInput]}
				value={name}
				onChange={e => {
					setName(e.target.value);
					debouncedSave({ name: e.target.value });
				}}
				onBlur={onNameBlur}
			/>
			<ColorPicker
				color={color}
				onColorChange={value => {
					setColor(value);
					saveImmediately({ color: value });
				}}
			/>
			<Input
				aria-label={`Maximum per day for ${category.name}`}
				styles={[styleSheet.maxInput]}
				type='number'
				min={0}
				value={maxPerDay}
				onChange={e => {
					setMaxPerDay(e.target.value);
					debouncedSave({ maxPerDay: toMaxValue(e.target.value) });
				}}
			/>
			<Input
				aria-label={`Maximum per month for ${category.name}`}
				styles={[styleSheet.maxInput]}
				type='number'
				min={0}
				value={maxPerMonth}
				onChange={e => {
					setMaxPerMonth(e.target.value);
					debouncedSave({ maxPerMonth: toMaxValue(e.target.value) });
				}}
			/>
			<Button
				variant='destructive-secondary'
				size='sm'
				onClick={() => setIsConfirmingDelete(true)}
				isLoading={deleteMutation.isPending}
			>
				Delete
			</Button>
		</div>
	);
};
